import { MDB_TYPES, SUB_PHASE} from "./constants.js"
import { groupByKey } from "./utils.js"

export class ScenarioType {
    constructor(data) {
        const {
            id,
            name,
            schemaVersion,
            checklist,
            interventions,
            interventionPrompts,
            interventionPromptAnswers,
            interventionVariantAnswers,
            interventionMenuCategories,
            interventionMenuItems,
            medications,
            hierarchy,
            phaseData,
            ...remaining
        } = data
        // console.log(checklist)
        this.id = id
        this.name = name
        this.schemaVersion = schemaVersion
        this._checklist = checklist
        this._interventions = interventions
        this._interventionPrompts = interventionPrompts
        this._interventionPromptAnswers = interventionPromptAnswers
        this.interventionVariantAnswers = interventionVariantAnswers
        this._interventionMenuCategories = interventionMenuCategories
        this._interventionMenuItems = interventionMenuItems
        this._medications = medications
        this.checkListMetaData = this.genCheckListMetaData()
        this.uniqueIDs = this.genUniqueIDs()
        this.uniqueLabels = this.genUniqueLabels()
    }

    genUniqueIDs() {
        let ids = this._interventions.map(e => e.id)
        ids = ids.concat(this._interventionPrompts.map(e => e.id))
        ids = ids.concat(this._interventionPromptAnswers.map(e => e.id))
        ids = ids.concat(this._interventionMenuCategories.map(e => e.id))
        ids = ids.concat(this._interventionMenuItems.map(e => e.id))
        ids = ids.concat(this._medications.map(e => e.id))
        ids = ids.concat(this._checklist.map(e => e.id))
        return ids
    }

    genUniqueLabels() {
        let labels = this._interventions.map(e => e.label)
        labels = labels.concat(this._checklist.map(e => e.label))
        return labels
    }

    genCheckListMetaData() {
        const metaData = []
        const CHECKLIST = [...this._checklist].map(e => {
            // remove obsolete attributes
            delete e.promptID
            delete e.relation
            delete e.verboseLabel
            if (undefined === e.phase) e.phase = null
            if (undefined === e.subPhase) e.subPhase = null
            return e
        })
        const PHASES = CHECKLIST.filter(e => e.type === MDB_TYPES.PHASE)
        const SUB_PHASES = CHECKLIST.filter(e => e.type === MDB_TYPES.SUB_PHASE)
        const OPTIONS = CHECKLIST.filter(e => e.type === MDB_TYPES.DECISION_OPTION)
        const CL_ITEMS = CHECKLIST.filter(e => !PHASES.includes(e) && !SUB_PHASES.includes(e) && !OPTIONS.includes(e))
        const phaseSubPhases = groupByKey(SUB_PHASES, "phase")
        PHASES.forEach(p => {
            if (p.id in phaseSubPhases) {
                p.subPhases = phaseSubPhases[p.id].map(e => e.id)
            }
        })
        const decisionOpts = groupByKey(OPTIONS, "parentID")
        CL_ITEMS.forEach(i => {
            if (i.type === MDB_TYPES.DECISION) {
                i.options = decisionOpts[i.id].map(o => o.id)
            }
        })
        const clDepth = {}
        CL_ITEMS.forEach(e => {
            const rel = (!!e.subPhase) ? e.subPhase : e.phase
            if (rel === e.parentID) {
                clDepth[e.id] = 1
            } else {
                clDepth[e.id] = clDepth[e.parentID] + 1
            }
            if (e.type === MDB_TYPES.NESTED_CHECKLIST) {
                e.type = MDB_TYPES.CL_HEADING
            }
            e.parentID = rel
            e.depth = clDepth[e.id]
        })
        const clItemsBySubPhase = groupByKey(CL_ITEMS, "subPhase")
        const phaseItems = [...clItemsBySubPhase[null]]
        const itemsByPhase = groupByKey(phaseItems, "phase")
        PHASES.forEach(p => {
            if (p.id in itemsByPhase) {
                p.items = itemsByPhase[p.id].map(i => i.id)
            }
        })
        delete clItemsBySubPhase[null]
        SUB_PHASES.forEach(sp => sp.items = clItemsBySubPhase[sp.id].map(i => i.id))
        //
        return CHECKLIST
    }

    genHierarchy() {
        const CHECKLIST = [...this._checklist].map(e => {
            // remove obsolete attributes
            delete e.promptID
            delete e.relation
            delete e.verboseLabel
            return e
        })
        const PHASES = CHECKLIST.filter(e => e.type === MDB_TYPES.PHASE)
        const SUB_PHASES = CHECKLIST.filter(e => e.type === MDB_TYPES.SUB_PHASE)
        const OPTIONS = CHECKLIST.filter(e => e.type === MDB_TYPES.DECISION_OPTION)
        const CL_ITEMS = CHECKLIST.filter(e => !PHASES.includes(e) && !SUB_PHASES.includes(e))
        CL_ITEMS.forEach(e => {
            if (undefined === e.subPhase) {
                // FIXME: we should set this up in the data-structure itself
                e.subPhase = null
            }
        })
        const phaseSubPhases = groupByKey(SUB_PHASES, "phase")
        PHASES.forEach(p => {
            if (p.id in phaseSubPhases) {
                p.subPhases = phaseSubPhases[p.id].map(e => e.id)
            }
        })
        const decisionOpts = groupByKey(OPTIONS, "parentID")
        CL_ITEMS.forEach(i => {
            if (i.type === MDB_TYPES.DECISION) {
                i.options = decisionOpts[i.id].map(o => o.id)
            }
        })
        const clDepth = {}
        CL_ITEMS.forEach(e => {
            const rel = (!!e.subPhase) ? e.subPhase : e.phase
            if (rel === e.parentID) {
                clDepth[e.id] = 1
            } else {
                clDepth[e.id] = clDepth[e.parentID] + 1
            }
            if (e.type === MDB_TYPES.NESTED_CHECKLIST) {
                e.type = MDB_TYPES.CL_HEADING
            }
            e.parentID = rel
            e.depth = clDepth[e.id]
        })
        const clItemsBySubPhase = groupByKey(CL_ITEMS, "subPhase")
        const phaseItems = [...clItemsBySubPhase[null]]
        const itemsByPhase = groupByKey(phaseItems, "phase")
        PHASES.forEach(p => {
            if (p.id in itemsByPhase) {
                p.items = itemsByPhase[p.id].map(i => i.id)
            }
        })
        delete clItemsBySubPhase[null]
        SUB_PHASES.forEach(sp => sp.items = clItemsBySubPhase[sp.id].map(i => i.id))
        const hierarchy = []
        PHASES.forEach(ph => {
            const {subPhases, items, ...newPh} = ph
            if (undefined !== items) {
                newPh.items = items.map(itemID => {
                    const {options, ...newItem} = CL_ITEMS.find(e => e.id === itemID)
                    if (undefined !== options) {
                        newItem.options = options.map(optID => OPTIONS.find(o => o.id === optID))
                    }
                    return newItem
                })
                hierarchy.push(newPh)
            } else {
                newPh.subPhases = subPhases.map(sphID => {
                    const {items, ...newSph} = SUB_PHASES.find(sp => sp.id === sphID)
                    newSph.items = items.map(itemID => {
                      const {options, ...newItem} =  CL_ITEMS.find(i => i.id === itemID)
                      if (undefined !== options) {
                          newItem.options = options.map(optID => OPTIONS.find(o => o.id === optID))
                      }
                      return newItem
                    })
                    return newSph
                })
                hierarchy.push(newPh)
            }
        })
        return hierarchy
    }

    getChecklistItem(id) {
        return this._checklist.find(e => e.id === id)
    }

    getInterventionForm(interventionID) {
        return this.getIntervention(interventionID, true)
    }

    getIntervention(interventionID, expand=false) {
        const found = this._interventions.find(e => e.id === interventionID)
        if (!found) { return {}}
        const {distPrompts, prompts, ...intv} = found
        if (!expand) {
            intv.distPrompts = distPrompts
            intv.prompts = prompts
            return intv
        }
        intv.distPrompts = distPrompts.map(distPromptID => this.getPrompt(distPromptID, true))
        intv.prompts = prompts.map(promptID => this.getPrompt(promptID, true))
        return intv
    }

    getDecisionWithOptions(id) {
        const found = this.getChecklistItem(id)
        if (!found) {
            return {}
        }
        const {options, ...decision} = found
        // console.log(decision)
        decision.options = options.map(optID => this.getChecklistItem(optID))
        return decision
    }

    getPrompt(promptID, expand=false) {
        const found = this._interventionPrompts.find(e => e.id === promptID)
        if (!found) { return {}}
        const {answers, ...prompt} = found
        if (!expand) {
            prompt.answers = answers
        } else {
            prompt.answers = answers.map(answerID => this.getAnswer(answerID))
        }
        return prompt
    }

    getAnswer(answerID) {
        return this._interventionPromptAnswers.find(e => e.id === answerID)
    }

    getInterventionMenus() {
        return this._interventionMenuCategories.map(e => {
            const {items, ...newRec} = e
            newRec.items = items.map(itemID => this._interventionMenuItems.find(mi => mi.id === itemID))
            return newRec
        })
    }
}

export class PublishedScenario {

    constructor(data) {
        const {
            id,
            name,
            version,
            datePublished,
            scenarioType,
            schemaVersion,
            info,
            vitalSigns,
            OPQRST,
            SAMPLE,
            assessmentFindings,
            checkListCorrectness,
            correctMedicationDosages,
            problems,
            solutions,
            actions,
            interventionVariantAnswers,
            checkListMetaData,
            medications,
            interventions,
            interventionPrompts,
            interventionPromptAnswers,
            interventionMenuCategories,
            interventionMenuItems,
            ...rest
        } = data
        this.id = id
        this.name = name
        this.version = version
        this.datePublished = datePublished
        this.scenarioType = scenarioType
        this.schemaVersion = schemaVersion
        this.info = info
        this.vitalSigns = vitalSigns
        this.OPQRST = OPQRST
        this.SAMPLE = SAMPLE
        this.assessmentFindings = assessmentFindings
        this.checkListCorrectness = checkListCorrectness
        this.correctMedicationDosages = correctMedicationDosages
        this.problems = problems
        this.solutions = solutions
        this.actions = actions
        this.interventionVariantAnswers = interventionVariantAnswers
        this._checkListMetaData = checkListMetaData
        this.medications = medications
        this._interventions = interventions
        this._interventionPrompts = interventionPrompts
        this._interventionPromptAnswers = interventionPromptAnswers
        this._interventionMenuCategories = interventionMenuCategories
        this._interventionMenuItems  = interventionMenuItems
        // console.log(rest)
        this.phaseData = this.genPhaseData()
        this.interventionMenus = this.genInterventionMenus()
        this.systemInterventions = this.genSystemInterventions()
    }

    genSystemInterventions() {
        let tmp = this._interventions
                    .filter(e => !!e.system)
                    .map(e => {return {id: e.id, type: MDB_TYPES.INTERV, label: e.label, system: e.system}})
        const sysIntvs = groupByKey(tmp, "system")
        // console.log(sysIntvs)
        return sysIntvs
    }
    genInterventionMenus() {
        return this._interventionMenuCategories.map(cat => {
            const {items, ...menuCat} = cat
            menuCat.items = items.map(itemID => this._interventionMenuItems.find(mi => mi.id === itemID))
            return menuCat
        })
    }
    genPhaseData() {
        const CHECKLIST = [...this._checkListMetaData].map(e => {
            // remove obsolete attributes
            delete e.promptID
            delete e.relation
            delete e.verboseLabel
            return e
        })
        const PHASES = CHECKLIST.filter(e => e.type === MDB_TYPES.PHASE)
        const SUB_PHASES = CHECKLIST.filter(e => e.type === MDB_TYPES.SUB_PHASE)
        const OPTIONS = CHECKLIST.filter(e => e.type === MDB_TYPES.DECISION_OPTION)
        const CL_ITEMS = CHECKLIST.filter(e => !PHASES.includes(e) && !SUB_PHASES.includes(e) && !OPTIONS.includes(e))
        CL_ITEMS.forEach(e => {
            if (undefined === e.subPhase) {
                // FIXME: we should set this up in the data-structure itself
                e.subPhase = null
            }
        })
        const phaseSubPhases = groupByKey(SUB_PHASES, "phase")
        PHASES.forEach(p => {
            if (p.id in phaseSubPhases) {
                p.subPhases = phaseSubPhases[p.id].map(e => e.id)
            }
        })
        const decisionOpts = groupByKey(OPTIONS, "parentID")
        CL_ITEMS.forEach(i => {
            if (i.type === MDB_TYPES.DECISION) {
                i.options = decisionOpts[i.id].map(o => o.id)
            }
        })
        const clDepth = {}
        CL_ITEMS.forEach(e => {
            const rel = (!!e.subPhase) ? e.subPhase : e.phase
            if (rel === e.parentID) {
                clDepth[e.id] = 1
            } else {
                clDepth[e.id] = clDepth[e.parentID] + 1
            }
            if (e.type === MDB_TYPES.NESTED_CHECKLIST) {
                e.type = MDB_TYPES.CL_HEADING
            }
            e.parentID = rel
            e.depth = clDepth[e.id]
        })
        const clItemsBySubPhase = groupByKey(CL_ITEMS, "subPhase")
        const phaseItems = [...clItemsBySubPhase[null]]
        const itemsByPhase = groupByKey(phaseItems, "phase")
        PHASES.forEach(p => {
            if (p.id in itemsByPhase) {
                p.items = itemsByPhase[p.id].map(i => i.id)
            }
        })
        delete clItemsBySubPhase[null]
        // console.log(clItemsBySubPhase)
        SUB_PHASES.forEach(sp => {
            // console.log(sp)
            const items = clItemsBySubPhase[sp.id].map(i => i.id)
            // console.log()
            sp.items = items
        })
        const hierarchy = []
        PHASES.forEach(ph => {
            const {
                subPhases,
                items,
                ...newPh
            } = ph
            if (undefined !== items) {
                newPh.items = items.map(itemID => {
                    const {
                        options,
                        ...newItem
                    } = CL_ITEMS.find(e => e.id === itemID)
                    if (undefined !== options) {
                        newItem.options = options.map(optID => OPTIONS.find(o => o.id === optID))
                    }
                    return newItem
                })
                hierarchy.push(newPh)
            } else {
                newPh.subPhases = subPhases.map(sphID => {
                    const {
                        items,
                        ...newSph
                    } = SUB_PHASES.find(sp => sp.id === sphID)
                    newSph.items = items.map(itemID => {
                        const {
                            options,
                            ...newItem
                        } = CL_ITEMS.find(i => i.id === itemID)
                        if (undefined !== options) {
                            newItem.options = options.map(optID => OPTIONS.find(o => o.id === optID))
                        }
                        return newItem
                    })
                    return newSph
                })
                hierarchy.push(newPh)
            }
        })
        return hierarchy
    }

    getChecklistItem(id) {
        return this._checkListMetaData.find(e => e.id === id)
    }

    getInterventionForm(intvID) {
        return this.getIntervention(intvID, true)
    }

    getIntervention(interventionID, expand = false) {
        const found = this._interventions.find(e => e.id === interventionID)
        if (!found) {
            return {}
        }
        const {
            distPrompts,
            prompts,
            ...intv
        } = found
        if (!expand) {
            intv.distPrompts = distPrompts
            intv.prompts = prompts
            return intv
        }
        intv.distPrompts = distPrompts.map(distPromptID => this.getPrompt(distPromptID, true))
        intv.prompts = prompts.map(promptID => this.getPrompt(promptID, true))
        return intv
    }

    getPrompt(promptID, expand = false) {
        const found = this._interventionPrompts.find(e => e.id === promptID)
        if (!found) {
            return {}
        }
        const {
            answers,
            ...prompt
        } = found
        if (!expand) {
            prompt.answers = answers
        } else {
            prompt.answers = answers.map(answerID => this.getAnswer(answerID))
        }
        return prompt
    }

    getAnswer(answerID) {
        return this._interventionPromptAnswers.find(e => e.id === answerID)
    }

}